
import { useEffect, useState } from "react";

// context
import { useAuth } from "@/auth/AuthContext";

// axios
import { api } from "@/api/axios";

// mui
import { Box, Typography } from "@mui/material";

// project
import ModalWindow from "components/modal/ModalWindow";
import ActionButton from "components/ActionButton";
import ConfirmInput from "components/ConfirmInput";
import CodeInput from "components/CodeInput";
import NotificationSnackbar from "components/NotificationSnackbar";

// НАЧАЛО ПРАВКИ №1
// type: "email" | "phone"
// open, onClose, onConfirmed передаются родительским компонентом.
export default function RequireContactModal({
  type = "email",
  open = false,
  onClose,
  onConfirmed
}) {
  const isPhone = type === "phone";

  const {
    setIsEmailRequired
  } = useAuth();

  // Существующие маршруты email сохраняем.
  // Маршруты телефона нужно реализовать на сервере.
  const endpoints = isPhone
    ? {
        send: "/gates/data/send_phone",
        resend: "/gates/data/resend_code_phone",
        confirm: "/gates/data/confirm_code_phone"
      }
    : {
        send: "/gates/data/send_email",
        resend: "/gates/data/resend_code_email",
        confirm: "/gates/data/confirm_code_email"
      };
  // КОНЕЦ ПРАВКИ №1

  const [requestId, setRequestId] = useState(null);
  const [isGetCode, setIsGetCode] = useState(false);

  // НАЧАЛО ПРАВКИ №2
  // Универсальное значение контакта.
  const [contactValue, setContactValue] = useState("");
  // КОНЕЦ ПРАВКИ №2

  const [codeValue, setCodeValue] = useState("");
  const [resendSeconds, setResendSeconds] = useState(60);
  const [isInputCodeBlock, setIsInputCodeBlock] = useState(false);

  // НАЧАЛО ПРАВКИ №3
  // Блокировка повторных запросов во время отправки.
  const [isSending, setIsSending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  // КОНЕЦ ПРАВКИ №3

  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "error"
  });

  const showError = (message) => {
    setNotification({
      open: true,
      message: message || "Произошла ошибка. Попробуйте позже.",
      severity: "error"
    });
  };

  // НАЧАЛО ПРАВКИ №4
  // Валидация телефона и email.
  const normalizedContact = contactValue.trim();

  const isEmailValid =
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedContact);

  const phoneDigits = normalizedContact.replace(/\D/g, "");

  const normalizedPhone =
    phoneDigits.length === 11 && phoneDigits.startsWith("7")
      ? `8${phoneDigits.slice(1)}`
      : phoneDigits;

  const isPhoneValid = /^8\d{10}$/.test(normalizedPhone);

  const isContactValid = isPhone
    ? isPhoneValid
    : isEmailValid;

  const contactPayload = isPhone
    ? { PHONE: normalizedPhone }
    : { EMAIL: normalizedContact };

  const getContactPayload = () => contactPayload;

  const maskEmail = (email) => {
    if (!email || !email.includes("@")) return "";

    const [name, domain] = email.split("@");

    return `${name.slice(0, 2)}***@${domain}`;
  };

  const maskPhone = (phone) => {
    const digits = phone.replace(/\D/g, "");

    if (digits.length !== 11) return phone;

    return `+7 (***) ***-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  };

  const maskedContact = isPhone
    ? maskPhone(normalizedPhone)
    : maskEmail(normalizedContact);
  // КОНЕЦ ПРАВКИ №4

  const getBack = () => {
    setIsGetCode(false);
    setCodeValue("");
    setResendSeconds(60);
    setRequestId(null);
    setIsInputCodeBlock(false);
  };

  // НАЧАЛО ПРАВКИ №5
  // При смене типа подтверждения и закрытии окна
  // сбрасываем состояние предыдущего запроса.
  useEffect(() => {
    setRequestId(null);
    setIsGetCode(false);
    setContactValue("");
    setCodeValue("");
    setResendSeconds(60);
    setIsInputCodeBlock(false);
    setIsSending(false);
    setIsConfirming(false);
    setNotification({
      open: false,
      message: "",
      severity: "error"
    });
  }, [type, open]);
  // КОНЕЦ ПРАВКИ №5

  // НАЧАЛО ПРАВКИ №6
  // Отправка кода на выбранный контакт.
  const sendCode = async () => {
    if (isSending || !isContactValid) return;

    setIsSending(true);

    try {
      const res = await api.post(
        endpoints.send,
        getContactPayload()
      );

      const data = res.data;

      if (data?.SUCCESS === true && data?.DATA?.REQUEST_ID) {
        setRequestId(data.DATA.REQUEST_ID);
        setIsGetCode(true);
        setCodeValue("");
        setResendSeconds(60);
        setIsInputCodeBlock(false);
      } else {
        const typeError = data?.ERROR?.CODE;

        showError(data?.ERROR?.MESSAGE);

        if (
          !isPhone &&
          typeError === "EMAIL_ALREADY_SET"
        ) {
          setIsEmailRequired(false);
        }
      }
    } catch (error) {
      console.error("sendCode error:", error);
      showError("Не удалось отправить код подтверждения.");
    } finally {
      setIsSending(false);
    }
  };
  // КОНЕЦ ПРАВКИ №6

  // НАЧАЛО ПРАВКИ №7
  // Подтверждение кода.
  const onComplete = async (enteredCode) => {
    if (isConfirming || !requestId) return;

    setIsConfirming(true);

    try {
      const payload = {
        REQUEST_ID: requestId,
        CODE: enteredCode
      };

      const res = await api.post(
        endpoints.confirm,
        payload
      );

      const data = res.data;

      if (data?.SUCCESS === true) {
        if (!isPhone) {
          setIsEmailRequired(false);
        }

        // Родитель обновляет PHONE_REQUIRED / EMAIL_REQUIRED
        // и закрывает соответствующую модалку.
        onConfirmed?.();
        return;
      }

      const typeError =
        data?.ERROR?.CODE ?? data?.CODE;

      const messageError =
        data?.ERROR?.MESSAGE ?? data?.MESSAGE;

      showError(messageError);

      if (typeError === "RELOAD_TIMEOUT") {
        setIsInputCodeBlock(true);
      }

      if (
        typeError === "CANCEL" ||
        typeError === "EMAIL_BIND_FAILED" ||
        typeError === "PHONE_BIND_FAILED"
      ) {
        getBack();
      }
    } catch (error) {
      console.error("onComplete error:", error);
      showError("Не удалось подтвердить код.");
    } finally {
      setIsConfirming(false);
    }
  };
  // КОНЕЦ ПРАВКИ №7

  // НАЧАЛО ПРАВКИ №8
  // Повторная отправка.
  const resendCode = async () => {
    if (resendSeconds > 0 || isSending) return;

    setIsSending(true);

    try {
      const res = await api.post(
        endpoints.resend,
        getContactPayload()
      );

      const data = res.data;

      if (data?.SUCCESS === true && data?.DATA?.REQUEST_ID) {
        setRequestId(data.DATA.REQUEST_ID);
        setResendSeconds(60);
        setCodeValue("");
        setIsInputCodeBlock(false);
      } else {
        showError(data?.ERROR?.MESSAGE);
      }
    } catch (error) {
      console.error("resendCode error:", error);
      showError("Не удалось повторно отправить код.");
    } finally {
      setIsSending(false);
    }
  };
  // КОНЕЦ ПРАВКИ №8

  // Таймер.
  useEffect(() => {
    if (!open || !isGetCode || resendSeconds <= 0) return;

    const timer = setTimeout(
      () => setResendSeconds((prev) => Math.max(0, prev - 1)),
      1000
    );

    return () => clearTimeout(timer);
  }, [open, resendSeconds, isGetCode]);

  const canResend = resendSeconds === 0 && !isSending;

  const minutes = Math.floor(resendSeconds / 60);
  const seconds = resendSeconds % 60;

  const timer = `${minutes}:${String(seconds).padStart(2, "0")}`;

  return (
    <ModalWindow
      open={open}
      onClose={onClose}
      maxWidth="sm"
      title={
        isPhone
          ? "Подтвердите номер телефона"
          : "Прикрепите почту к учетной записи"
      }
    >
      <Box>
        {!isGetCode ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Typography>
              {isPhone
                ? "Укажите номер телефона, на который будет отправлен SMS-код подтверждения. Это обязательно."
                : "Укажите почту, которую хотите привязать к аккаунту, на нее будет отправлен код подтверждения. Это обязательно."}
            </Typography>

            <ConfirmInput
              value={contactValue}
              label={isPhone ? "Телефон" : "Email"}
              onChange={(e) =>
                setContactValue(e.target.value)
              }
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: "1rem"
              }}
            >
              <ActionButton
                children={
                  isSending ? "Отправка..." : "Отправить код"
                }
                onClick={sendCode}
                disabled={!isContactValid || isSending}
                sx={{
                  width: { xs: "100%", md: "fit-content" }
                }}
              />
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1
            }}
          >
            <Typography>
              {isPhone ? (
                <>
                  На номер телефона{" "}
                  <strong>{maskedContact}</strong>{" "}
                  отправлен SMS-код подтверждения.
                  Введите полученный код ниже.
                </>
              ) : (
                <>
                  На электронную почту{" "}
                  <strong>{maskedContact}</strong>{" "}
                  было отправлено письмо с кодом подтверждения.
                  Введите полученный код ниже.
                </>
              )}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                my: "1rem"
              }}
            >
              <CodeInput
                value={codeValue}
                onChange={setCodeValue}
                onComplete={onComplete}
                isInputCodeBlock={
                  isInputCodeBlock || isConfirming
                }
              />
            </Box>

            <Typography
              onClick={canResend ? resendCode : undefined}
              sx={{
                fontSize: "0.875rem",
                textAlign: "center",
                fontWeight: "bold",
                color: canResend ? "rgd.primary" : "rgd.text1",
                cursor: canResend ? "pointer" : "not-allowed",
                userSelect: "none",
                "&:hover": {
                  textDecoration: canResend
                    ? "underline"
                    : "none"
                }
              }}
            >
              {canResend
                ? "Получить новый код"
                : `Через ${timer} можно получить новый код`}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: "1rem"
              }}
            >
              <ActionButton
                children="Назад"
                onClick={getBack}
                sx={{
                  width: { xs: "100%", md: "fit-content" }
                }}
              />
            </Box>
          </Box>
        )}

        <NotificationSnackbar
          open={notification.open}
          message={notification.message}
          severity={notification.severity}
          onClose={() =>
            setNotification((prev) => ({
              ...prev,
              open: false
            }))
          }
        />
      </Box>
    </ModalWindow>
  );
}
